/* eslint-disable react-refresh/only-export-components -- co-located helpers/constants */
import React from 'react';
import {
  InputAdornment,
  type TextFieldProps,
} from '@mui/material';
import { AppTextField } from '../design-system';
import { Email } from '@mui/icons-material';

interface EmailFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  autoComplete?: string;
  name?: string;
  inputRef?: TextFieldProps['inputRef'];
  sx?: TextFieldProps['sx'];
}

/**
 * Reusable Email Input Component with consistent validation
 * 
 * Validation Rules:
 * - Valid email format (name@domain.ext)
 * - Automatically converts to lowercase while typing
 * - Trims whitespace on blur
 * 
 * @example
 * ```tsx
 * const [email, setEmail] = useState('');
 * const [emailError, setEmailError] = useState('');
 * 
 * const validateEmail = (value: string) => {
 *   if (!value && required) return 'Email is required';
 *   if (value && !isValidEmail(value)) return 'Invalid email format';
 *   return '';
 * };
 * 
 * <EmailField
 *   value={email}
 *   onChange={(value) => {
 *     setEmail(value);
 *     setEmailError(validateEmail(value));
 *   }}
 *   error={!!emailError}
 *   helperText={emailError}
 *   required
 * />
 * ```
 */
export const EmailField: React.FC<EmailFieldProps> = ({
  value,
  onChange,
  onBlur,
  label = 'Email Address',
  placeholder,
  required = false,
  disabled = false,
  error = false,
  helperText,
  fullWidth = true,
  autoComplete = 'email',
  name = 'email',
  inputRef,
  sx,
}) => {
  const [internalError, setInternalError] = React.useState<string>('');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Lowercase while typing; trim only on blur so the caret stays put mid-edit.
    const newValue = event.target.value.toLowerCase();
    onChange(newValue);

    const trimmedForValidation = newValue.trim();
    if (trimmedForValidation && !isValidEmail(trimmedForValidation)) {
      setInternalError('Please enter a valid email address (e.g., user@example.com)');
    } else if (required && !trimmedForValidation) {
      setInternalError('Email is required');
    } else {
      setInternalError('');
    }
  };

  const handleBlur = () => {
    const trimmedValue = value?.trim().toLowerCase() || '';
    if (trimmedValue !== value) {
      onChange(trimmedValue);
    }

    if (required && !trimmedValue) {
      setInternalError('Email is required');
    } else if (trimmedValue && !isValidEmail(trimmedValue)) {
      setInternalError('Please enter a valid email address (e.g., user@example.com)');
    } else {
      setInternalError('');
    }
    
    if (onBlur) {
      onBlur();
    }
  };

  const displayError = error || !!internalError;
  const displayHelperText = helperText || internalError;

  return (
    <AppTextField
      name={name}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      inputRef={inputRef}
      label={label}
      type="email"
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      error={displayError}
      helperText={displayHelperText}
      fullWidth={fullWidth}
      autoComplete={autoComplete}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Email fontSize="small" sx={{ color: 'text.secondary' }} />
          </InputAdornment>
        ),
      }}
      sx={sx}
    />
  );
};

/**
 * Validates email format
 * @param email - Email string to validate
 * @returns true if valid email format
 */
export const isValidEmail = (email: string): boolean => {
  if (!email) return false;
  
  // More comprehensive email validation
  // RFC 5322 compliant email regex (simplified version)
  const emailRegex = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;
  
  // Additional checks
  const trimmed = email.trim();
  if (trimmed.length > 254) return false; // Max email length per RFC 5321
  if (trimmed.includes('..')) return false; // No consecutive dots
  if (trimmed.startsWith('.') || trimmed.endsWith('.')) return false; // No leading/trailing dots
  
  const parts = trimmed.split('@');
  if (parts.length !== 2) return false; // Must have exactly one @
  
  const [localPart, domain] = parts;
  if (localPart.length > 64) return false; // Local part max length
  if (domain.length < 3) return false; // Domain too short (a.b minimum)
  
  return emailRegex.test(trimmed);
};

/**
 * Validates email with error message
 * @param email - Email string to validate
 * @param required - Whether email is required
 * @returns Error message or empty string if valid
 */
export const validateEmail = (email: string, required: boolean = false): string => {
  const trimmedEmail = email?.trim() || '';
  
  if (!trimmedEmail) {
    return required ? 'Email is required' : '';
  }
  
  if (!isValidEmail(trimmedEmail)) {
    return 'Please enter a valid email address';
  }
  
  return '';
};

export default EmailField;
